import * as Sentry from "@sentry/react";
import Keycloak from "keycloak-js";
import React from "react";
import { SplashScreen } from "../../common/SplashScreen.js";
import { ROUTER_BASENAME } from "../../environment.js";
import { deviceGrantTokenStorage } from "./deviceGrantTokenStorage.js";
import { AuthContext, tokenBelongsToGroup } from "./AuthContext.js";
import { SIGNUP_SCOPE } from "./signup/signupScope.js";
import { installSignupRoleRequestParam } from "./signupRoleRequestParam.js";
import { buildSilentCheckSsoRedirectUri } from "./silentCheckSsoRedirectUri.js";

interface Props extends React.PropsWithChildren<{}> {
  keycloak: Keycloak;
}

export const AuthWebProvider = ({ children, keycloak }: Props) => {
  const [isReady, setIsReady] = React.useState(false);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);

  React.useEffect(() => {
    function setIsAuthenticatedState() {
      setIsAuthenticated(keycloak.authenticated ?? false);
    }

    // A tab signed in through the device grant keeps its tokens in
    // sessionStorage. Logout clears them here, before keycloak-js redirects,
    // so every logout caller is covered.
    const originalLogout = keycloak.logout;
    keycloak.logout = options => {
      deviceGrantTokenStorage.clear();
      return originalLogout.call(keycloak, options);
    };

    function persistRefreshedDeviceGrantTokens() {
      setIsAuthenticatedState();
      if (
        deviceGrantTokenStorage.has() &&
        keycloak.token &&
        keycloak.refreshToken
      ) {
        deviceGrantTokenStorage.write({
          token: keycloak.token,
          refreshToken: keycloak.refreshToken,
          idToken: keycloak.idToken,
        });
      }
    }

    function dropDeviceGrantTokens() {
      deviceGrantTokenStorage.clear();
      setIsAuthenticatedState();
    }

    async function bootstrap() {
      try {
        installSignupRoleRequestParam(keycloak);
        keycloak.onReady = () => {
          setIsReady(true);
        };
        keycloak.onAuthError = setIsAuthenticatedState;
        keycloak.onAuthLogout = setIsAuthenticatedState;
        keycloak.onAuthRefreshError = dropDeviceGrantTokens;
        keycloak.onTokenExpired = setIsAuthenticatedState;
        keycloak.onAuthSuccess = setIsAuthenticatedState;
        keycloak.onAuthRefreshSuccess = persistRefreshedDeviceGrantTokens;
        const deviceGrantTokens = deviceGrantTokenStorage.read();
        await keycloak.init({
          onLoad: "check-sso",
          // keycloak-js >= 25 defaults this to false; set explicitly. Polling
          // the KC session-status iframe fails without third-party storage
          // access (Chrome / incognito / ITP), which cleared the token and
          // caused an infinite login loop (MEIS-12157 / MEIS-12771).
          checkLoginIframe: false,
          silentCheckSsoRedirectUri: buildSilentCheckSsoRedirectUri(
            location.origin,
            ROUTER_BASENAME
          ),
          silentCheckSsoFallback: true,
          // Default scope of every login/registration URL, including the
          // automatic redirect for unauthenticated routes: a visitor without
          // an account gets the company fields on the registration page.
          scope: SIGNUP_SCOPE,
          ...(deviceGrantTokens ?? {}),
        });
      } catch (e) {
        if (e instanceof Error)
          console.error("Failed to initialize keycloak: ", e.message);
        Sentry.captureException(e);
      }
    }

    void bootstrap();

    return () => {
      keycloak.logout = originalLogout;
    };
  }, [keycloak]);

  if (!isReady) return <SplashScreen />;

  return (
    <AuthContext.Provider
      value={{
        isReady,
        isAuthenticated,
        belongsToGroup: (groupName: string) =>
          tokenBelongsToGroup(keycloak.idTokenParsed, groupName),
        auth: keycloak,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
